import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Form, Field } from 'react-final-form';
import { makeStyles } from 'tss-react/mui';

import { Button, Grid, units } from '@hbf/dsl';
import { availabilityToDateRage } from '@ha/date';
import { useIntl } from 'ha/i18n';
import { SmartTrack as Track } from 'ha/components/track/SmartTrack';
import { useFeatureFlags } from 'ha/modules/FeatureFlags';
import { Experiment, Variant } from 'ha/modules/Experiments';
import { Experiments } from 'ha/constants/experiments';

import { LocationsInput } from './LocationsInput';
import { submitSearch } from '../../actions';
import { getInitialValues } from '../../selectors';
import { HomeDateRangePicker as DateRangePicker } from './DateRangePicker';
import { validateForm } from './validateForm';
import { HomeMoveInSelector } from './MoveInSelector';
import { HomeDurationSelector } from './DurationSelector';

const useStyles = makeStyles()(theme => ({
  container: {
    padding: units.yx(0.25, 0.5),
  },
  button: {
    background: theme.palette.primary.main,
    boxShadow: 'inherit',
    '&:hover': {
      boxShadow: '0 2px 9px 0 rgba(146, 83, 134, 0.35)',
    },
    height: units.rem(3.5),
    fontSize: units.rem(1.125),
    fontWeight: 'bold',
  },
}));

export interface FormValues {
  place: string;
  moveIn?: string;
  duration?: number;
  dateRange: {
    startDate?: string | null;
    endDate?: string | null;
  };
}

const AvailabilityFilter: React.FC = () => {
  return (
    <React.Fragment>
      <Experiment name={Experiments.TNT478.name}>
        <Variant name="A">
          <React.Fragment>
            <Grid item md={2} xs={6}>
              <Field name="moveIn" component={HomeMoveInSelector} />
            </Grid>
            <Grid item md={2} xs={6}>
              <Field name="duration" component={HomeDurationSelector} />
            </Grid>
          </React.Fragment>
        </Variant>
        <Variant name="B">
          <Grid item xs={12} md={4}>
            <Field name="dateRange" component={DateRangePicker} />
          </Grid>
        </Variant>
      </Experiment>
    </React.Fragment>
  );
};

export const SearchBar: React.FC = () => {
  const { T, lang, urlResolver } = useIntl();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const initialValues = useSelector(getInitialValues);
  const { flexibleSearchDates } = useFeatureFlags();
  const isFlexibleSearchDatesEnabled = flexibleSearchDates === 'on';

  const onSubmit = React.useCallback((formValues: FormValues) => {
    if (isFlexibleSearchDatesEnabled && formValues.moveIn) {
      formValues.dateRange = availabilityToDateRage({
        moveIn: {
          type: 'date',
          value: formValues.moveIn,
        },
        duration: formValues.duration,
      });
    }

    dispatch(submitSearch({ formValues, lang, urlResolver }));
  }, []);

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      validate={values => validateForm(values, T)}
    >
      {({ handleSubmit, submitting, values }) => (
        <form onSubmit={handleSubmit}>
          <Grid container spacing={1} className={classes.container}>
            <Grid item xs={12} md={6}>
              <Field
                name="place"
                id="place"
                component={LocationsInput}
                placeholder={T('Where will you go?')}
                predictionTypes={['(cities)']}
                dataTestLocator="Location Input Box"
              />
            </Grid>

            {isFlexibleSearchDatesEnabled ? (
              <AvailabilityFilter />
            ) : (
              <Grid item xs={12} md={4}>
                <Field name="dateRange" component={DateRangePicker} />
              </Grid>
            )}

            <Grid item xs={12} md={2}>
              <Track
                name="Clicked Search button"
                type="button"
                getDynamicData={() => values}
              >
                <Button
                  disabled={submitting}
                  type="submit"
                  fullWidth
                  data-test-locator="Search and book"
                  variant="contained"
                  color="primary"
                  classes={{
                    root: classes.button,
                  }}
                >
                  {T('Search')}
                </Button>
              </Track>
            </Grid>
          </Grid>
        </form>
      )}
    </Form>
  );
};
